import type { TQuestionAnswer } from '$/types/components/questions';
import type { InputProps } from '../Inputs/TextInput';

import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback } from 'react';

import Button from '../Button';
import ErrorMessage from '../ErrorMessage';

import List from './List';
import ListItem from './ListItem';

import theme from '$/assets/theme';

export interface ISelectAnswer {
  list: TQuestionAnswer[];
  fieldProps: {
    name: string;
    value: TQuestionAnswer[];
    onChange: (d: any) => void;
  };
  // Maximum number of selected values, 0 for infinity
  maxElements?: number;
  collapse?: boolean;
  hide?: boolean;
  boldedText?: string;
  error?: InputProps['error'];
  errorMessage?: InputProps['errorMessage'];
  buttonFocusIndex?: number;
  buttonProps?: IButtonProps['buttonProps'];
}

const boldText = (text: string, boldedText?: string): React.ReactNode => {
  if (!boldedText) return text;
  const index = text.toLowerCase().indexOf(boldedText.toLowerCase());
  if (index === -1) return text;

  return (
    <>
      <span>{text.slice(0, index)}</span>
      <b>{text.slice(index, boldedText.length + index)}</b>
      <span>{text.slice(boldedText.length + index)}</span>
    </>
  );
};

const SelectAnswer = ({
  maxElements = 0,
  collapse = false,
  hide = false,
  buttonProps,
  buttonFocusIndex,
  ...props
}: ISelectAnswer) => {
  const value = props.fieldProps?.value || [];

  const toggleAnswer = useCallback(
    (answer: TQuestionAnswer | undefined) => {
      if (!answer) {
        return;
      }

      let values = [...value];
      if (values.find((a) => a.id === answer.id)) {
        props.fieldProps?.onChange(values.filter((a) => a.id !== answer.id));
        return;
      }

      if (maxElements === 0 || values.length < maxElements) {
        values.push(answer);
        props.fieldProps?.onChange(values);
        return;
      }

      if (values.length >= maxElements) {
        values = values.slice(1);
        values.push(answer);
        props.fieldProps?.onChange(values);
      }
    },
    [value, props.fieldProps, maxElements]
  );

  const handleOnClick = useCallback(
    (e: React.ChangeEvent<HTMLElement>) => {
      const answerId = e.currentTarget.dataset.id;
      if (answerId) {
        const selectedItem = props.list.find((a) => a.id == answerId);
        toggleAnswer(
          selectedItem || {
            id: e.currentTarget.dataset.id,
            value: e.currentTarget.dataset.name,
            subjectId: e.currentTarget.dataset.subject,
            isEuropean: e.currentTarget.dataset.isEuropean
              ? Number(e.currentTarget.dataset.isEuropean)
              : 0,
          }
        );
      }
    },
    [toggleAnswer, props.list]
  );

  return (
    <>
      <List
        id={props.fieldProps?.name}
        style={{ display: hide ? 'none' : 'grid' }}
      >
        {props.list.map((listItem, index) => {
          const selected = Array.isArray(value)
            ? value?.some((d) => d.id === listItem.id)
            : value;
          return (
            <ListItem
              data-hide={collapse && value.length >= maxElements && !selected}
              key={listItem.id}
            >
              <Button
                variant="answer"
                focus={
                  buttonFocusIndex ? index === buttonFocusIndex - 1 : false
                }
                title={
                  listItem.value === listItem.name &&
                  listItem.value === listItem.id ? (
                    <>
                      <strong>,,{listItem.value}''</strong>&nbsp;
                      <span>übernehmen</span>
                    </>
                  ) : (
                    boldText(listItem.value, props.boldedText)
                  )
                }
                buttonProps={{
                  'data-elbaction': listItem['data-elbaction'],
                  ...buttonProps,
                  onClick: handleOnClick,
                  'data-id': listItem.id,
                  'data-name': listItem.name || listItem.value,
                  'data-subject': listItem.subjectId || null,
                  'data-is-european': listItem.isEuropean || null,
                  selected: selected,
                  style: {
                    height: 'calc(100% - 27px)',
                  },
                }}
                rightIcon={
                  selected && (
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      size="lg"
                      color={theme.colors.brandGreen}
                      style={{
                        background: theme.colors.white,
                      }}
                    />
                  )
                }
              />
            </ListItem>
          );
        })}
      </List>
      {props.error && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
    </>
  );
};

export default SelectAnswer;
